import * as React from "react";
import { cn } from "@/lib/utils";

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  glow?: boolean;
  brackets?: boolean; // luminous cyan corner brackets (What-If HUD style)
  featured?: boolean; // brighter border + glow (recommended card)
}

function Corner({ pos }: { pos: "tl" | "tr" | "bl" | "br" }) {
  const base =
    "pointer-events-none absolute h-5 w-5 border-[#7FD4FF]/80 [filter:drop-shadow(0_0_6px_rgba(127,212,255,0.8))]";
  const map: Record<string, string> = {
    tl: "left-2 top-2 border-l border-t",
    tr: "right-2 top-2 border-r border-t",
    bl: "left-2 bottom-2 border-l border-b",
    br: "right-2 bottom-2 border-r border-b",
  };
  return <span className={cn(base, map[pos])} />;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    { className, glow = false, brackets = false, featured = false, children, ...props },
    ref,
  ) => (
    <div
      ref={ref}
      className={cn(
        "relative rounded-2xl border bg-[linear-gradient(180deg,rgba(14,22,40,0.78),rgba(7,11,20,0.9))] p-6 backdrop-blur-xl transition-[border-color,box-shadow] duration-300",
        "shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_30px_80px_-40px_rgba(0,0,0,0.9),0_0_50px_-30px_rgba(46,123,255,0.45)]",
        featured
          ? "border-[#4d8fff]/60 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_0_50px_-12px_rgba(46,123,255,0.65)]"
          : "border-[#2E7BFF]/22 hover:border-[#2E7BFF]/40",
        !brackets &&
          "before:pointer-events-none before:absolute before:inset-x-10 before:top-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-[#7FD4FF]/60 before:to-transparent",
        glow && "glow-cyan-soft",
        className,
      )}
      {...props}
    >
      {brackets && (
        <>
          <Corner pos="tl" />
          <Corner pos="tr" />
          <Corner pos="bl" />
          <Corner pos="br" />
        </>
      )}
      {children}
    </div>
  ),
);
Card.displayName = "Card";

export const CardHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("mb-4 space-y-1", className)} {...props} />
);

export const CardTitle = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h3
    className={cn(
      "font-display text-lg font-bold uppercase tracking-wider text-foreground",
      className,
    )}
    {...props}
  />
);

export const CardDescription = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn("text-sm text-muted-foreground", className)} {...props} />
);

export const CardContent = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("text-sm text-foreground/90", className)} {...props} />
);
